/**
 * Scraping Detection View
 * Focused dashboard for identifying and ranking suspicious access patterns.
 * Shows suspicion scores, flags, severity distribution, and user ranking.
 */
import * as echarts from 'echarts'
import { useEffect, useRef, useState } from 'preact/hooks'
import {
  getCellAccessAnalytics,
  getCellAccessOverview,
} from '../../../services/adminService.js'
import { COLORS, chartTheme } from './shared/chartTheme.js'
import {
  BlockedCount,
  FLAG_DESCRIPTIONS,
  RefusedBadge,
  SuspicionBadge,
  TierBadge,
  UserDetailPanel,
} from './shared/cellAccessCharts.jsx'
import { formatNumber } from './shared/formatters.js'
import { Icons } from './shared/Icons.jsx'
import { KPICard } from './shared/KPICard.jsx'

/**
 * Severity donut chart — distribution of users by suspicion band
 */
function SeverityDonut({ data, height = 220 }) {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!chartRef.current || !data) return

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' })
    }

    const pieces = [
      { name: 'Critical (71-100)', value: data.critical || 0, itemStyle: { color: '#ef4444' } },
      { name: 'Warning (46-70)', value: data.warning || 0, itemStyle: { color: COLORS.orange } },
      { name: 'Low (26-45)', value: data.low || 0, itemStyle: { color: '#f59e0b' } },
      { name: 'Clean (0-25)', value: data.clean || 0, itemStyle: { color: '#22c55e' } },
    ].filter((p) => p.value > 0)

    const option = {
      ...chartTheme,
      tooltip: { ...chartTheme.tooltip, trigger: 'item', formatter: '{b}: {c} users ({d}%)' },
      series: [{
        type: 'pie',
        radius: ['45%', '75%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: true,
        label: { show: true, color: COLORS.gray, fontSize: 11, formatter: '{b}\n{c}' },
        data: pieces,
      }],
    }

    chartInstance.current.setOption(option)
    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [data])

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null }, [])

  if (!data) return null

  return (
    <div className="analytics-chart-container">
      <div className="analytics-chart-header">
        <span className="analytics-chart-title">Users by Suspicion Level</span>
      </div>
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  )
}

/**
 * User scatter plot — coverage vs volume, colored & sized by suspicion
 */
function UserScatterPlot({ users, height = 300 }) {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!chartRef.current || !users || users.length === 0) return

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' })
    }

    const getColor = (score) => {
      if (score >= 71) return '#ef4444'
      if (score >= 46) return COLORS.orange
      if (score >= 26) return '#f59e0b'
      return '#22c55e'
    }

    // Y axis plots ATTEMPTS, not served volume: a rate-limited scraper served 0
    // rows would otherwise sit on the x-axis as a big red dot at height zero,
    // which reads as a scoring bug rather than as a blocked attack.
    const scatterData = users.map((u) => ({
      value: [
        u.unique_cells_accessed,
        u.attempted_full_requests ?? u.full_cell_requests ?? 0,
        u.suspicion_score,
        u.full_cell_requests ?? 0,
        u.blocked_full_requests ?? 0,
      ],
      name: u.email,
      itemStyle: { color: getColor(u.suspicion_score) },
      symbolSize: Math.max(8, Math.min(30, u.suspicion_score / 3)),
    }))

    const option = {
      ...chartTheme,
      grid: { left: 60, right: 30, top: 20, bottom: 40 },
      tooltip: {
        ...chartTheme.tooltip,
        trigger: 'item',
        formatter: (p) =>
          `${p.name}<br/>Unique Cells: ${p.value[0]}<br/>Attempted: ${p.value[1]}<br/>Served: ${p.value[3]}<br/>Blocked: ${p.value[4]}<br/>Score: ${p.value[2]}`,
      },
      xAxis: {
        type: 'value',
        name: 'Unique Cells Accessed',
        nameLocation: 'center',
        nameGap: 25,
        nameTextStyle: { color: COLORS.gray, fontSize: 12 },
        axisLabel: { color: COLORS.gray },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      yAxis: {
        type: 'value',
        name: 'Attempted Full Requests',
        nameTextStyle: { color: COLORS.gray, fontSize: 12 },
        axisLabel: { color: COLORS.gray },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      series: [{ type: 'scatter', data: scatterData }],
    }

    chartInstance.current.setOption(option)
    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [users])

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null }, [])

  if (!users || users.length === 0) return null

  return (
    <div className="analytics-chart-container">
      <div className="analytics-chart-header">
        <span className="analytics-chart-title">User Coverage vs Volume</span>
        <span style={{ fontSize: '11px', color: COLORS.gray }}>
          Y = attempted (served or refused) &middot; size &amp; color = suspicion score
        </span>
      </div>
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  )
}

/**
 * Flag frequency bar chart — how often each suspicion flag occurs
 */
function FlagFrequencyChart({ users, height = 260 }) {
  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  useEffect(() => {
    if (!chartRef.current || !users || users.length === 0) return

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, null, { renderer: 'canvas' })
    }

    // Count flag occurrences
    const flagCounts = {}
    users.forEach((u) => {
      (u.suspicion_flags || []).forEach((flag) => {
        flagCounts[flag] = (flagCounts[flag] || 0) + 1
      })
    })

    const sorted = Object.entries(flagCounts)
      .sort(([, a], [, b]) => a - b)
    const labels = sorted.map(([flag]) => flag.replace(/_/g, ' '))
    const values = sorted.map(([, count]) => count)

    const option = {
      ...chartTheme,
      grid: { left: 180, right: 30, top: 10, bottom: 20 },
      tooltip: {
        ...chartTheme.tooltip,
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const flag = sorted[params[0].dataIndex][0]
          return `<strong>${flag}</strong><br/>Triggered by ${params[0].value} users<br/><em>${FLAG_DESCRIPTIONS[flag] || ''}</em>`
        },
      },
      xAxis: {
        type: 'value',
        axisLabel: { color: COLORS.gray },
        splitLine: { lineStyle: { color: COLORS.grid } },
      },
      yAxis: {
        type: 'category',
        data: labels,
        axisLabel: { color: COLORS.gray, fontSize: 10 },
      },
      series: [{
        type: 'bar',
        data: values.map((v) => ({
          value: v,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: '#ef444480' },
              { offset: 1, color: '#ef4444' },
            ]),
          },
        })),
        barWidth: '60%',
      }],
    }

    chartInstance.current.setOption(option)
    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [users])

  useEffect(() => () => { chartInstance.current?.dispose(); chartInstance.current = null }, [])

  if (!users || users.length === 0) return null

  // Check if any flags exist
  const hasFlags = users.some((u) => u.suspicion_flags && u.suspicion_flags.length > 0)
  if (!hasFlags) return null

  return (
    <div className="analytics-chart-container">
      <div className="analytics-chart-header">
        <span className="analytics-chart-title">Most Common Suspicion Flags</span>
        <span style={{ fontSize: '11px', color: COLORS.gray }}>
          Number of users triggering each flag
        </span>
      </div>
      <div ref={chartRef} style={{ width: '100%', height }} />
    </div>
  )
}

/**
 * Main Scraping Detection View
 */
export function AnalyticsScrapingView({ periodDays }) {
  const [data, setData] = useState(null)
  const [overview, setOverview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [sortBy, setSortBy] = useState('suspicion_score')
  const [sortDir, setSortDir] = useState('desc')
  const [expandedUser, setExpandedUser] = useState(null)
  const [filterLevel, setFilterLevel] = useState('all') // all, critical, warning, low, clean

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    Promise.all([
      getCellAccessAnalytics({ period_days: periodDays, limit: 200 }),
      getCellAccessOverview(periodDays).catch(() => null),
    ])
      .then(([result, overviewResult]) => {
        if (!cancelled) {
          setData(result)
          setOverview(overviewResult)
        }
      })
      .catch((err) => {
        if (!cancelled) setError(err.message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => { cancelled = true }
  }, [periodDays])

  if (loading) {
    return (
      <div className="analytics-loading">
        <div className="spinner"></div>
        <p>Loading scraping detection data...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="analytics-error">
        <span>{Icons.alertTriangle}</span>
        <p>{error}</p>
      </div>
    )
  }

  if (!data) return null

  const items = data.items || []

  // Severity counts
  const critical = items.filter((u) => u.suspicion_score >= 71).length
  const warning = items.filter((u) => u.suspicion_score >= 46 && u.suspicion_score < 71).length
  const low = items.filter((u) => u.suspicion_score >= 26 && u.suspicion_score < 46).length
  const clean = items.filter((u) => u.suspicion_score < 26).length

  // Filter by level
  const filtered = items.filter((u) => {
    if (filterLevel === 'all') return true
    if (filterLevel === 'critical') return u.suspicion_score >= 71
    if (filterLevel === 'warning') return u.suspicion_score >= 46 && u.suspicion_score < 71
    if (filterLevel === 'low') return u.suspicion_score >= 26 && u.suspicion_score < 46
    if (filterLevel === 'clean') return u.suspicion_score < 26
    return true
  })

  // Sort. `attempted_full_requests` falls back to the served count so the column
  // sorts on the number it renders even against an API response without the
  // attempt split.
  const sortValue = (u, col) => {
    if (col === 'flags_count') return (u.suspicion_flags || []).length
    if (col === 'attempted_full_requests') {
      return u.attempted_full_requests ?? u.full_cell_requests ?? 0
    }
    return u[col] ?? 0
  }

  const sorted = [...filtered].sort((a, b) => {
    const aVal = sortValue(a, sortBy)
    const bVal = sortValue(b, sortBy)
    if (sortDir === 'asc') return aVal > bVal ? 1 : aVal < bVal ? -1 : 0
    return aVal < bVal ? 1 : aVal > bVal ? -1 : 0
  })

  const handleSort = (col) => {
    if (sortBy === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(col)
      setSortDir('desc')
    }
  }

  const sortIndicator = (col) => {
    if (sortBy !== col) return ''
    return sortDir === 'asc' ? ' \u25B2' : ' \u25BC'
  }

  const severityData = overview?.flagged_users_by_severity || { critical, warning, low, clean }

  return (
    <div className="analytics-scraping-view">
      {/* KPI Summary */}
      <div className="analytics-kpi-grid">
        <KPICard
          icon={Icons.users}
          label="Users Analyzed"
          value={formatNumber(items.length)}
          sublabel={`of ${formatNumber(data.total)} total`}
          color="cyan"
        />
        <KPICard
          icon={Icons.alertTriangle}
          label="Critical"
          value={formatNumber(critical)}
          sublabel="Score 71-100"
          color={critical > 0 ? 'red' : 'green'}
        />
        <KPICard
          icon={Icons.zap}
          label="Warning"
          value={formatNumber(warning)}
          sublabel="Score 46-70"
          color={warning > 0 ? 'orange' : 'green'}
        />
        <KPICard
          icon={Icons.check}
          label="Clean"
          value={formatNumber(clean)}
          sublabel="Score 0-25"
          color="green"
        />
      </div>

      {/* Charts row: Severity donut + Scatter + Flag frequency */}
      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '16px', marginBottom: '16px' }}>
        <SeverityDonut data={severityData} />
        <UserScatterPlot users={items} />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <FlagFrequencyChart users={items} />
      </div>

      {/* IP Concentration warning */}
      {overview?.ip_concentration && overview.ip_concentration.length > 0 && (
        <div className="analytics-chart-container" style={{ marginBottom: '16px', padding: '16px' }}>
          <div className="analytics-chart-header">
            <span className="analytics-chart-title">Multi-IP Users</span>
            <span style={{ fontSize: '11px', color: COLORS.gray }}>
              Users accessing from 3+ distinct IPs
            </span>
          </div>
          <table className="analytics-table" style={{ marginTop: '8px' }}>
            <thead>
              <tr>
                <th>Email</th>
                <th className="count-cell">Distinct IPs</th>
                <th className="count-cell">Countries</th>
              </tr>
            </thead>
            <tbody>
              {overview.ip_concentration.map((row) => (
                <tr key={row.user_id}>
                  <td>{row.email}</td>
                  <td className="count-cell">{row.distinct_ips}</td>
                  <td className="count-cell">{row.distinct_countries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* User ranking table */}
      <div className="analytics-table-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <h3 style={{ margin: 0 }}>User Suspicion Ranking</h3>
          <div className="scraping-filter-pills">
            {[
              { id: 'all', label: 'All', count: items.length },
              { id: 'critical', label: 'Critical', count: critical, color: '#ef4444' },
              { id: 'warning', label: 'Warning', count: warning, color: COLORS.orange },
              { id: 'low', label: 'Low', count: low, color: '#f59e0b' },
              { id: 'clean', label: 'Clean', count: clean, color: '#22c55e' },
            ].map((pill) => (
              <button
                key={pill.id}
                className={`scraping-filter-pill ${filterLevel === pill.id ? 'active' : ''}`}
                onClick={() => setFilterLevel(pill.id)}
                style={filterLevel === pill.id && pill.color ? { borderColor: pill.color, color: pill.color } : {}}
              >
                {pill.label} ({pill.count})
              </button>
            ))}
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="analytics-table cell-access-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }} className="count-cell">#</th>
                <th className="sortable-th" onClick={() => handleSort('email')}>
                  User{sortIndicator('email')}
                </th>
                <th>Tier</th>
                <th className="sortable-th count-cell" onClick={() => handleSort('suspicion_score')}>
                  Score{sortIndicator('suspicion_score')}
                </th>
                <th className="sortable-th count-cell" onClick={() => handleSort('flags_count')}>
                  Flags{sortIndicator('flags_count')}
                </th>
                <th
                  className="sortable-th count-cell"
                  onClick={() => handleSort('full_cell_requests')}
                  title="Full cell-data requests that were actually SERVED (2xx/3xx)."
                >
                  Full Requests{sortIndicator('full_cell_requests')}
                </th>
                <th
                  className="sortable-th count-cell"
                  onClick={() => handleSort('attempted_full_requests')}
                  title="Full cell-data requests ATTEMPTED, served or not — this is what the suspicion score is built on."
                >
                  Attempted{sortIndicator('attempted_full_requests')}
                </th>
                <th
                  className="sortable-th count-cell"
                  onClick={() => handleSort('blocked_full_requests')}
                  title="Attempted full-data requests that were refused (429 rate limit / 401 / 403). A high score with 0 served and many blocked means the abuse was stopped, not absent."
                >
                  Blocked{sortIndicator('blocked_full_requests')}
                </th>
                <th className="sortable-th count-cell" onClick={() => handleSort('unique_cells_accessed')}>
                  Unique Cells{sortIndicator('unique_cells_accessed')}
                </th>
                <th className="sortable-th count-cell" onClick={() => handleSort('active_days')}>
                  Active Days{sortIndicator('active_days')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((user, idx) => {
                const isExpanded = expandedUser === user.user_id
                const flags = user.suspicion_flags || []
                return [
                  <tr
                    key={user.user_id}
                    className={`cell-access-row ${isExpanded ? 'cell-access-row--expanded' : ''}`}
                    onClick={() => setExpandedUser(isExpanded ? null : user.user_id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="count-cell" style={{ color: COLORS.gray, fontSize: '12px' }}>
                      {idx + 1}
                    </td>
                    <td>
                      <div className="cell-access-user-info">
                        <span className="cell-access-user-name">
                          {user.full_name || 'Unknown'}
                        </span>
                        <span className="cell-access-user-email">{user.email}</span>
                        {user.company && (
                          <span className="cell-access-user-company">{user.company}</span>
                        )}
                        <RefusedBadge
                          attempted={user.attempted_full_requests}
                          blocked={user.blocked_full_requests}
                        />
                      </div>
                    </td>
                    <td><TierBadge tier={user.subscription_tier} /></td>
                    <td className="count-cell">
                      <SuspicionBadge score={user.suspicion_score} />
                    </td>
                    <td className="count-cell">
                      <span style={{ color: flags.length > 0 ? '#ef4444' : COLORS.gray }}>
                        {flags.length}
                      </span>
                    </td>
                    <td className="count-cell">{formatNumber(user.full_cell_requests)}</td>
                    <td className="count-cell">
                      {formatNumber(user.attempted_full_requests ?? user.full_cell_requests ?? 0)}
                    </td>
                    <td className="count-cell">
                      <BlockedCount
                        attempted={user.attempted_full_requests}
                        blocked={user.blocked_full_requests}
                      />
                    </td>
                    <td className="count-cell">{formatNumber(user.unique_cells_accessed)}</td>
                    <td className="count-cell">{user.active_days}</td>
                  </tr>,
                  isExpanded && (
                    <tr key={`${user.user_id}-detail`} className="cell-access-detail-row">
                      <td colSpan={10}>
                        {/* Suspicion flags */}
                        {flags.length > 0 && (
                          <div className="cell-access-flags">
                            <strong style={{ color: 'var(--text-primary)' }}>
                              Suspicion Flags:
                            </strong>
                            <ul className="cell-access-flag-list">
                              {flags.map((flag) => (
                                <li key={flag}>
                                  <span className="cell-access-flag-name">{flag}</span>
                                  <span className="cell-access-flag-desc">
                                    {FLAG_DESCRIPTIONS[flag] || flag}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <UserDetailPanel userId={user.user_id} periodDays={periodDays} />
                      </td>
                    </tr>
                  ),
                ]
              })}
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: '24px' }}>
                    No users match the selected filter
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
